import React from 'react';
import PageHeader from '../../components/PageHeader';
import PayInvoicesModal from '../../components/PayInvoicesModal';
import SearchBar from '../../components/SearchBar';
import DateRangeFilter from '../../components/DateRangeFilter';
import { qbInvoiceUrl } from '../../utils/qbInvoiceUrl';
import { HEADER_CELL } from '../../utils/tableColumns';
import { money } from '../../utils/money';
import {
  amountBreakdown, PAGE_SIZE, PAYMENT_STATUS, payable, pdfAvailable, qbDay, SORT_KEYS, STATUS,
} from './invoiceLedger';

const count = (n) => Number(n || 0).toLocaleString('en-US');
const date = (d) => (d ? new Date(d).toLocaleDateString('en-US') : '?');
const rowDate = (inv, d) => (inv.source === 'quickbooks' ? qbDay(d) : date(d));

function agoPhrase(t, iso) {
  if (!iso) return '';
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return t('just now');
  if (mins < 60) return t('{n} min ago', { n: mins });
  const hrs = Math.round(mins / 60);
  return t(hrs === 1 ? '{n} hour ago' : '{n} hours ago', { n: hrs });
}

function syncedAgo(t, iso) {
  if (!iso) return t('not synced yet');
  return t('synced {ago}', { ago: agoPhrase(t, iso) });
}

const LABEL = 'text-[11px] font-semibold uppercase tracking-[0.08em] text-[#64748b]';

export default function InvoiceLedgerView({
  t, restricted, admin, isClinicUser, search,
  setSearch, dates, setDates, handleSync, syncing,
  openPay, selectedRows, paymentsOn, downloadingPdf, selectedClinicName,
  selectedTotal, pdfError, sandbox, payConfig, paidBanner,
  setPaidBanner, payCtx, closePay, handlePaid, setSelected,
  fetchData, summary, scopeLabel, syncFailed, meta,
  tabs, tabListProps, getTabProps, setActiveTab, activeTab,
  clinicId, setClinicId, clinics, error, panelProps,
  loaded, loading, showSelectAll, selectAllTargetName, downloadPdf,
  pdfReady, selectAllForClinic, selectAllCandidates, selectAllLabel, pdfFlight,
  rows, COLUMNS, columns, sort, toggleSort,
  selected, canTick, toggle, retryingId, handleRetry,
  from, to, setPage, page, payments,
  paymentsLoading, workingId, handlePaymentAction,
}) {
  if (restricted) {
    return (
      <div>
        <PageHeader title={t('Invoices')} subtitle={t('Invoices and payments for your practice.')} />
        <div className="bg-white border border-[#e2e8f0] rounded-[14px] shadow-sm p-8 text-center text-sm text-[#64748b]">
          {t('Invoices are available to the practice owner and to MedEffects staff. Ask your practice owner to pay or review invoices.')}
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title={t('Invoices')} subtitle={admin ? t('Every QuickBooks invoice for every clinic, history included.') : isClinicUser ? t('Every QuickBooks invoice for your clinic, history included.') : t('Every QuickBooks invoice for your clinics, history included.')}>
        <SearchBar placeholder={t('Search invoices...')} className="w-[243px]" value={search} onChange={(e) => setSearch(e.target.value)} />
        <DateRangeFilter dateFrom={dates.dateFrom} dateTo={dates.dateTo} onChange={setDates} />
        {admin && (
          <button
            onClick={handleSync}
            disabled={syncing}
            title={t('Pull the latest invoices and balances from QuickBooks now')}
            className="h-[46px] px-4 border border-[#e2e8f0] bg-white rounded-[10px] text-sm font-semibold text-[#24315d] hover:bg-gray-50 disabled:opacity-50 inline-flex items-center gap-2"
          >
            <span className="material-symbols-outlined text-[18px]">sync</span>
            {syncing ? t('Syncing...') : t('Sync from QuickBooks')}
          </button>
        )}
        {(
          <button
            onClick={openPay}
            disabled={selectedRows.length === 0 || !paymentsOn || downloadingPdf}
            title={
              !paymentsOn
                ? t('Online payments are not available right now')
                : selectedRows.length === 0
                  ? t('Tick the open invoices to pay first')
                  : t(selectedRows.length === 1 ? 'Pay {n} invoice for {clinic}' : 'Pay {n} invoices for {clinic}', { n: selectedRows.length, clinic: selectedClinicName })
            }
            className="h-[46px] px-5 bg-[#007a55] text-white text-sm font-semibold rounded-[10px] hover:bg-[#00694a] transition-colors disabled:opacity-50"
          >
            {selectedRows.length > 0 ? t('Pay selected · {amount}', { amount: money(selectedTotal) }) : t('Pay selected')}
          </button>
        )}
      </PageHeader>

      {pdfError && <div role="alert" className="mb-4 rounded-[10px] border border-red-200 bg-red-50 p-3 text-sm text-red-700">{t(pdfError)}</div>}
      {downloadingPdf && <div role="status" className="mb-4 text-sm text-[#24315d]">{t('Preparing PDF...')}</div>}

      {sandbox && (
        <div className="mb-4 p-2 rounded-lg text-[12px] bg-[#fffbeb] border border-[#fde68a] text-[#b45309] font-semibold">
          {t('TEST MODE - payments go to the QuickBooks sandbox, not a real merchant account.')}
        </div>
      )}

      {payConfig && !paymentsOn && (
        <div role="status" className="mb-4 p-3 rounded-lg text-[13px] bg-[#f8fafc] border border-[#e2e8f0] text-[#475569]">
          {t('Online payments are not available right now')}. {t('Invoices can still be viewed.')}
        </div>
      )}

      {paidBanner && (
        <div role="status" className="mb-4 p-4 rounded-lg text-sm bg-green-50 border border-green-200 text-green-800 flex items-start justify-between gap-4">
          <div>
            <p className="font-semibold">
              {t('{amount} paid for {clinic}', { amount: money(paidBanner.amount), clinic: paidBanner.clinic?.name || t('the clinic') })}
              {paidBanner.invoices?.length ? ` · ${t(paidBanner.invoices.length === 1 ? 'invoice {list}' : 'invoices {list}', { list: paidBanner.invoices.map((i) => i.docNumber || i.qbInvoiceId).join(', ') })}` : ''}
            </p>
            {paidBanner.settlesLater && <p className="mt-1 text-[12px] text-green-700">{t('Bank payments settle in a few business days.')}</p>}
            {paidBanner.recordingPending && (
              <p className="mt-1 text-[12px] text-[#b45309]">{t('The payment went through; QuickBooks will show it once our team finishes the record.')}</p>
            )}
          </div>
          <button type="button" onClick={() => setPaidBanner(null)} aria-label={t('Dismiss')} className="text-green-700 hover:text-green-900 shrink-0">
            <span className="material-symbols-outlined text-[20px]">close</span>
          </button>
        </div>
      )}

      {payCtx && payConfig && (
        <PayInvoicesModal
          invoices={payCtx.invoices}
          clinicName={payCtx.clinicName}
          config={payConfig}
          onClose={closePay}
          onPaid={handlePaid}
          onAmountChanged={() => { setSelected({}); fetchData(); }}
        />
      )}

      {/* 1. THE STATEMENT HEADER */}
      <section aria-label={t('Statement summary')} className="bg-white border border-[#e2e8f0] rounded-[14px] shadow-sm px-6 pt-5 pb-[18px] mb-[14px] flex flex-wrap items-end justify-between gap-6">
        <dl className="flex flex-wrap items-end gap-x-10 gap-y-5 m-0">
          <div>
            <dt className={LABEL}>{t('Outstanding')}</dt>
            <dd className="m-0 mt-1.5 text-[32px] leading-[1.05] font-bold tracking-[-0.02em] text-[#0f172a] tabular-nums">{money(summary?.outstanding ?? 0)}</dd>
            <dd className="m-0 mt-1 text-[12px] text-[#64748b]">
              {t(summary?.unpaidCount === 1 ? 'across {count} unpaid invoice · {scope}' : 'across {count} unpaid invoices · {scope}', { count: count(summary?.unpaidCount), scope: scopeLabel })}
            </dd>
          </div>
          <div>
            <dt className={LABEL}>{t('Overdue')}</dt>
            <dd className="m-0 mt-2 text-[20px] leading-[1.1] font-semibold tracking-[-0.01em] text-[#f23e41] tabular-nums">{money(summary?.overdueAmount ?? 0)}</dd>
            <dd className="m-0 mt-1 text-[12px] text-[#64748b]">{t(summary?.overdueCount === 1 ? '{count} invoice past due' : '{count} invoices past due', { count: count(summary?.overdueCount) })}</dd>
          </div>
          <div>
            <dt className={LABEL}>{t('Ledger')}</dt>
            <dd className="m-0 mt-2 text-[20px] leading-[1.1] font-semibold tracking-[-0.01em] text-[#0f172a] tabular-nums">
              {count(summary?.invoiceCount)} <span className="text-[14px] font-medium text-[#64748b]">{t(summary?.invoiceCount === 1 ? 'invoice' : 'invoices')}</span>
            </dd>
            <dd className="m-0 mt-1 text-[12px] text-[#64748b]">{summary?.firstYear ? t('{year} – today', { year: summary.firstYear }) : t('no invoices yet')}</dd>
          </div>
        </dl>
        <div
          className="inline-flex items-center gap-2 text-[12px] text-[#64748b] bg-[#f7f8fb] border border-[#e2e8f0] rounded-full pl-2.5 pr-3 py-1.5 ml-auto"
          title={syncFailed ? t('The last sync from QuickBooks failed') : t('Balances are as of this sync')}
        >
          <span className={`w-2 h-2 rounded-full ${syncFailed ? 'bg-[#f2b544] shadow-[0_0_0_3px_rgba(242,181,68,0.18)]' : 'bg-[#007a55] shadow-[0_0_0_3px_rgba(0,122,85,0.14)]'}`} />
          QuickBooks {syncFailed ? t('sync failed · last good {ago}', { ago: agoPhrase(t, meta.sync?.lastSuccessAt) || t('not synced yet') }) : syncedAgo(t, meta.sync?.lastSuccessAt)}
        </div>
      </section>

      {/* 2. THE VIEWS */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="inline-flex flex-wrap gap-1 bg-white border border-[#e2e8f0] rounded-[11px] p-1" {...tabListProps}>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              {...getTabProps(tab.key)}
              onClick={() => setActiveTab(tab.key)}
              className={`h-[34px] px-3.5 rounded-[8px] text-[13px] font-semibold transition-colors ${
                activeTab === tab.key ? 'bg-[#0f172a] text-white' : 'text-[#64748b] hover:text-[#0f172a]'
              }`}
            >
              {t(tab.label)}
            </button>
          ))}
        </div>
        {!isClinicUser && (
          <label className="flex items-center gap-2 text-[12px] text-[#64748b]">
            {t('Clinic')}
            <select
              value={clinicId}
              onChange={(e) => { setClinicId(e.target.value); setSelected({}); }}
              aria-label={t('Clinic')}
              className="h-9 pl-3 pr-8 border border-[#d6dce8] rounded-[9px] text-[13px] text-[#0f172a] bg-white focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20 focus:border-[#0089ff] min-w-[180px] max-w-[320px]"
            >
              <option value="">{t('All clinics')}</option>
              {clinics.map((c) => (
                <option key={c._id} value={c._id}>{c.name}</option>
              ))}
            </select>
          </label>
        )}
      </div>

      {error && <div role="alert" className="mb-4 p-3 rounded-lg text-sm bg-red-50 border border-red-200 text-red-600">{t(error)}</div>}

      {/* 3. THE TABLE */}
      <div {...panelProps}>
      {loading && !loaded ? (
        <div role="status" aria-live="polite" className="flex items-center justify-center h-40">
          <span className="sr-only">{t('Loading invoices...')}</span>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0089ff]" />
        </div>
      ) : (
        <>
          <div className={`bg-white border border-[#e2e8f0] rounded-[14px] shadow-sm overflow-x-auto transition-opacity ${loading ? 'opacity-60' : ''}`} aria-busy={loading}>
            {(selectedRows.length > 0 || showSelectAll) && (
              <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-2.5 border-b border-[#e2e8f0] bg-[#f0f7ff] text-[12px]">
                {selectedRows.length > 0 ? (
                  <span className="font-semibold text-[#0f172a] tabular-nums">
                    {t('{n} selected · {amount}', { n: selectedRows.length, amount: money(selectedTotal) })}
                    <span className="font-normal text-[#64748b]">{' '}{t('for {clinic}', { clinic: selectedClinicName })}</span>
                  </span>
                ) : (
                  <span className="text-[#64748b]">{t('Open invoices for {clinic} can be paid together.', { clinic: selectAllTargetName || t('this clinic') })}</span>
                )}
                <span className="flex flex-wrap items-center gap-4">
                  {selectedRows.length > 0 && (
                    <button type="button" onClick={() => downloadPdf(selectedRows)}
                      disabled={!pdfReady || downloadingPdf || !!payCtx || selectedRows.length > PAGE_SIZE}
                      title={t('Download the selected invoices together as one PDF.')}
                      className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-[8px] bg-[#0089ff] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#0077e6] disabled:opacity-40">
                      <span className="material-symbols-outlined text-[16px]" aria-hidden="true">download</span>
                      {downloadingPdf ? t('Preparing PDF...') : t(selectedRows.length === 1 ? 'Download {n} PDF' : 'Download {n} PDFs', { n: selectedRows.length })}
                    </button>
                  )}
                  {showSelectAll && (
                    <button type="button" onClick={selectAllForClinic} disabled={downloadingPdf} className="text-[#0089ff] font-medium hover:underline disabled:opacity-40" title={t('Tick every open invoice on this page for this clinic')}>
                      {selectedRows.length > 0 ? t('Select all {n}', { n: selectAllCandidates.length }) : selectAllLabel}
                    </button>
                  )}
                  {selectedRows.length > 0 && (
                    <button type="button" onClick={() => { if (!pdfFlight.current) setSelected({}); }} disabled={downloadingPdf} className="text-[#64748b] hover:text-[#0f172a] font-medium disabled:opacity-40">
                      {t('Clear')}
                    </button>
                  )}
                </span>
              </div>
            )}
            <div role="table" aria-label={t('Invoices')}>
            <div role="rowgroup" className="bg-[rgba(226,232,240,0.2)] border-b border-[#e2e8f0]">
              <div role="row" className={`grid ${COLUMNS} px-5 py-3`}>
                {columns.map((col, i) => {
                  const key = SORT_KEYS[col];
                  const active = key && sort.key === key;
                  const right = col === 'AMOUNT' || col === 'BALANCE';
                  const hint =
                    col === 'INVOICE #'
                      ? 'The number QuickBooks assigned, with the MedEffects order ID beneath it when the app created the invoice.'
                      : col === 'BALANCE'
                        ? 'What is still owed on this invoice, as of the last QuickBooks sync.'
                        : '';
                  if (!key) {
                    return (
                      <span role="columnheader" key={col || i} className={`${HEADER_CELL} ${right ? 'text-right pr-4' : ''}`}>
                        {t(col)}
                      </span>
                    );
                  }
                  // aria-sort is an ARIA token and stays English; only the tooltip's copy of it is translated.
                  const dirWord = sort.dir === 'asc' ? 'ascending' : 'descending';
                  return (
                    <span
                      key={col}
                      role="columnheader"
                      aria-sort={active ? dirWord : 'none'}
                      className={`${HEADER_CELL} ${right ? 'text-right pr-4 justify-end' : ''}`}
                    >
                      <button
                        type="button"
                        onClick={() => toggleSort(key)}
                        title={`${hint ? t(hint) + ' ' : ''}${t('Click to sort')}${active ? ` (${t(dirWord)})` : ''}`}
                        aria-label={t('Sort by {column}', { column: t(col).toLowerCase() })}
                        className={`inline-flex w-full items-center gap-1 hover:text-[#0f172a] ${right ? 'justify-end' : ''} ${active ? 'text-[#0f172a]' : ''}`}
                      >
                        {t(col)}
                        <span className={`material-symbols-outlined text-[14px] leading-none ${active ? 'text-[#0089ff]' : 'text-[#cbd5e1]'}`} aria-hidden="true">
                          {active ? (sort.dir === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more'}
                        </span>
                      </button>
                    </span>
                  );
                })}
              </div>
            </div>
            <div role="rowgroup" className="divide-y divide-[#e2e8f0]">
              {rows.length === 0 ? (
                <div role="row" className="px-5 py-8 text-center text-sm text-[#64748b]"><span role="cell">{t('No invoices found')}</span></div>
              ) : (
                rows.map((inv) => {
                  const sts = STATUS[inv.status] || STATUS.open;
                  const canRetry = admin && inv.source === 'app' && (inv.status === 'failed' || inv.status === 'needs_attention');
                  const invoiceNo = inv.docNumber || inv.orderId || '—';
                  const qbUrl = admin && inv.qbInvoiceId ? qbInvoiceUrl({ status: 'created', qbInvoiceId: inv.qbInvoiceId }) : null;
                  const tickable = paymentsOn && !downloadingPdf && canTick(inv);
                  return (
                    <div key={inv._id} role="row" className={`grid ${COLUMNS} px-5 py-[13px] items-center hover:bg-gray-50/50 transition-colors ${selected[inv._id] ? 'bg-[#f0f7ff]' : ''}`}>
                      <span role="cell">
                        {payable(inv) && (
                          <input
                            type="checkbox"
                            checked={!!selected[inv._id]}
                            disabled={!tickable}
                            onChange={() => toggle(inv)}
                            aria-label={t('Pay invoice {number}', { number: invoiceNo })}
                            title={
                              !paymentsOn
                                ? t('Online payments are not available right now')
                                : downloadingPdf
                                  ? t('Preparing PDF...')
                                  : tickable
                                    ? t('Include in the payment')
                                    : t('Finish paying {clinic} first - one clinic per payment', { clinic: selectedClinicName })
                            }
                            className="w-4 h-4 rounded border-[#d6dce8] text-[#0089ff] focus:ring-[#0089ff] disabled:opacity-40"
                          />
                        )}
                      </span>
                      <span role="cell" className="flex flex-col gap-0.5 min-w-0">
                        {qbUrl ? (
                          <a href={qbUrl} target="_blank" rel="noopener noreferrer" title={t('View in QuickBooks')} className="text-xs font-medium text-[#0089ff] underline hover:text-[#0077e6] truncate">
                            {invoiceNo}
                          </a>
                        ) : (
                          <span className="text-xs font-medium text-[#0f172a] truncate">{invoiceNo}</span>
                        )}
                        {inv.orderId && invoiceNo !== inv.orderId && <span className="text-[10px] font-medium text-[#94a3b8] truncate">{inv.orderId}</span>}
                      </span>
                      <span role="cell" className="text-xs font-medium text-[#0f172a] truncate" title={inv.clinic?.name || inv.customerName || ''}>
                        {inv.clinic?.name || inv.customerName || '—'}
                      </span>
                      <span role="cell" className="text-xs font-medium text-[#64748b] tabular-nums">{rowDate(inv, inv.txnDate)}</span>
                      <span role="cell" className={`text-xs font-medium tabular-nums ${inv.status === 'overdue' ? 'text-[#f23e41]' : 'text-[#64748b]'}`}>{rowDate(inv, inv.dueDate)}</span>
                      <span role="cell" title={amountBreakdown(inv, t)} className="text-xs font-semibold text-[#0089ff] text-right pr-4 tabular-nums">{inv.total ? money(inv.total) : '—'}</span>
                      <span role="cell" className={`text-xs font-semibold text-right pr-4 tabular-nums ${inv.balance > 0 ? 'text-[#0f172a]' : 'text-[#64748b]'}`}>{inv.balance == null ? '—' : money(inv.balance)}</span>
                      <span role="cell" title={admin && inv.lastError ? t('Contact MedEffects for help with this invoice.') : ''} className={`inline-flex items-center justify-center w-fit px-2.5 py-0.5 rounded-[6px] text-xs font-medium ${sts.bg} ${sts.text}`}>
                        {t(sts.label)}
                      </span>
                      <span role="cell" className="flex items-center gap-2 min-w-0">
                        {pdfAvailable(inv) ? (
                          <button type="button" onClick={() => downloadPdf([inv])} disabled={!pdfReady || downloadingPdf || !!payCtx}
                            aria-label={t('Download invoice {number} PDF', { number: invoiceNo })} title={t('Download invoice {number} PDF', { number: invoiceNo })}
                            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-[8px] border border-[#d6dce8] bg-white px-3 py-1.5 text-xs font-semibold text-[#24315d] hover:border-[#0089ff] hover:text-[#0089ff] disabled:opacity-40">
                            <span className="material-symbols-outlined text-[16px]" aria-hidden="true">download</span>
                            {downloadingPdf && pdfFlight.current?.invoiceIds.length === 1 && pdfFlight.current.invoiceIds[0] === inv._id ? t('Preparing PDF...') : t('Download PDF')}
                          </button>
                        ) : <span className="min-w-0 truncate text-xs text-[#94a3b8]" title={t('PDF available after this invoice is created in QuickBooks.')}>{t('PDF unavailable')}</span>}
                        {canRetry && (
                          <button
                            onClick={() => handleRetry(inv.appInvoiceId)}
                            disabled={retryingId === inv.appInvoiceId}
                            className="h-[30px] shrink-0 whitespace-nowrap px-3 bg-[#0089ff] text-white text-xs font-medium rounded-[6px] hover:bg-[#0077e6] transition-colors disabled:opacity-50"
                          >
                            {retryingId === inv.appInvoiceId ? t('Retrying...') : t('Retry')}
                          </button>
                        )}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
            </div>
          </div>
          <div className="flex items-center justify-between mt-[14px] text-[12px] text-[#64748b]">
            <span>
              {meta.total === 0
                ? t('No invoices')
                : t(meta.total === 1 ? 'Showing {from}–{to} of {total} invoice' : 'Showing {from}–{to} of {total} invoices', { from: count(from), to: count(to), total: count(meta.total) })}
              {meta.total > 0 && (summary ? meta.outstanding !== summary.outstanding : true) ? ` · ${t('{amount} outstanding in this view', { amount: money(meta.outstanding) })}` : ''}
            </span>
            {meta.totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className="h-[34px] px-3.5 border border-[#d6dce8] bg-white rounded-[8px] text-[13px] font-medium text-[#24315d] hover:bg-gray-50 disabled:opacity-40">
                  {t('Previous')}
                </button>
                <span className="tabular-nums px-1">{t('Page {page} of {total}', { page: meta.page, total: meta.totalPages })}</span>
                <button onClick={() => setPage((p) => Math.min(meta.totalPages, p + 1))} disabled={page >= meta.totalPages} className="h-[34px] px-3.5 border border-[#d6dce8] bg-white rounded-[8px] text-[13px] font-medium text-[#24315d] hover:bg-gray-50 disabled:opacity-40">
                  {t('Next')}
                </button>
              </div>
            )}
          </div>
          {activeTab === 'paid' && (payments.length > 0 || paymentsLoading) && (
            <section aria-label={t('Paid through MedEffects')} className="mt-8">
              <div className="flex items-baseline justify-between mb-3">
                <h2 className="text-[15px] font-bold text-[#0f172a] m-0">{t('Paid through MedEffects')}</h2>
                <span className="text-[12px] text-[#64748b]">{t('Bank account (ACH) payments taken in the app · debits are re-checked until they settle')}</span>
              </div>
              {paymentsLoading ? (
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0089ff]" />
          </div>
        ) : (
          <div className="bg-white border border-[#e2e8f0] rounded-[14px] shadow-sm overflow-x-auto">
            <div className="bg-[rgba(226,232,240,0.2)] border-b border-[#e2e8f0] rounded-t-[14px]">
              <div className="grid grid-cols-[minmax(90px,2fr)_minmax(116px,3fr)_minmax(72px,2fr)_minmax(90px,2fr)_minmax(120px,3fr)_minmax(120px,2.6fr)_120px] px-5 py-3">
                {['DATE', 'CLINIC', 'AMOUNT', 'METHOD', 'INVOICES', 'STATUS', ''].map((col, i) => (
                  <span key={col || i} className={HEADER_CELL}>{t(col)}</span>
                ))}
              </div>
            </div>
            <div className="divide-y divide-[#e2e8f0]">
              {payments.length === 0 ? (
                <div className="px-5 py-8 text-center text-sm text-[#64748b]">{t('No payments yet')}</div>
              ) : (
                payments.map((p) => {
                  const st = PAYMENT_STATUS[p.status] || PAYMENT_STATUS.initiated;
                  const inst = [p.instrument?.brand, p.instrument?.last4 ? t('ending {last4}', { last4: p.instrument.last4 }) : ''].filter(Boolean).join(' ');
                  const canCheck = admin && p.intuitTxnId && ['recorded', 'paid_unrecorded', 'unknown'].includes(p.status);
                  return (
                    <div key={p._id} className="grid grid-cols-[minmax(90px,2fr)_minmax(116px,3fr)_minmax(72px,2fr)_minmax(90px,2fr)_minmax(120px,3fr)_minmax(120px,2.6fr)_120px] px-5 py-4 items-center">
                      <span className="text-xs font-medium text-[#64748b]">{date(p.createdAt)}</span>
                      <span className="text-xs font-medium text-[#0f172a] truncate">{p.clinic?.name || '—'}</span>
                      <span className="text-xs font-semibold text-[#0f172a] tabular-nums">{money(p.amount)}</span>
                      <span className="text-xs font-medium text-[#64748b] truncate" title={inst}>
                        {t('Bank account')}{inst ? ` · ${inst}` : ''}
                      </span>
                      <span className="text-xs font-medium text-[#64748b] truncate" title={(p.invoices || []).map((i) => i.docNumber || i.qbInvoiceId).join(', ')}>
                        {(p.invoices || []).map((i) => i.docNumber || i.qbInvoiceId).join(', ') || '—'}
                      </span>
                      <span title={p.error || p.warning ? t('Payment details need review. Contact MedEffects for help.') : ''} className={`inline-flex items-center justify-center w-fit px-2.5 py-0.5 rounded-[6px] text-xs font-medium ${st.bg} ${st.text}`}>
                        {t(st.label)}{p.warning ? ` · ${t('review')}` : ''}
                      </span>
                      <span className="flex items-center gap-2">
                        {admin && p.status === 'paid_unrecorded' && (
                          <button onClick={() => handlePaymentAction(p, 'record')} disabled={workingId === p._id} className="h-[30px] px-3 bg-[#0089ff] text-white text-xs font-medium rounded-[6px] hover:bg-[#0077e6] disabled:opacity-50">
                            {workingId === p._id ? '...' : t('Record in QB')}
                          </button>
                        )}
                        {canCheck && p.status !== 'paid_unrecorded' && (
                          <button onClick={() => handlePaymentAction(p, 'refresh')} disabled={workingId === p._id} title={t('Ask Intuit how this transaction stands now')} className="h-[30px] px-3 border border-[#d6dce8] text-[#24315d] text-xs font-medium rounded-[6px] hover:bg-gray-50 disabled:opacity-50">
                            {workingId === p._id ? '...' : t('Check status')}
                          </button>
                        )}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
            </section>
          )}
        </>
      )}
      </div>
    </div>
  );
}
